import React, { type FC, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { TriggerTooltip, type ITriggerTooltipProps } from 'shared/ui/TriggerTooltip/TriggerTooltip'
import { Button, IconButton } from 'shared/ui'
import { encodeMergeFieldBraces, logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { useEntitiesContext } from 'entities/EntitiesExport'
import styles from './styles.module.scss'

type Props = {
  trigger?: HTMLAnchorElement
  onSuccess: () => void
  onAddShortLink: (value: string) => void
  onHideLinkTooltips: () => void
  isMarketing?: boolean
}

export const ShortenLinkTooltip: FC<ITriggerTooltipProps & Props> = observer(
  ({
    trigger,
    refVisibleTooltip,
    onLeaveTooltip,
    onSuccess,
    onAddShortLink,
    onHideLinkTooltips,
    isMarketing,
  }) => {
    const { shortLinkStore } = useEntitiesContext()

    useEffect(() => {
      shortLinkStore.initAllDomains()
    }, [])

    const onShorten = async () => {
      try {
        const originalLink = trigger?.textContent

        if (originalLink) {
          const shortUrl = await shortLinkStore.createPersonalizedShortLink(
            originalLink,
            isMarketing
          )

          if (shortUrl) {
            trigger.href = encodeMergeFieldBraces(shortUrl)
            trigger.innerText = shortUrl
            onSuccess()
          }
        }
      } catch (e) {
        logger.error(e)
      }
    }

    const onEdit = () => {
      onHideLinkTooltips()
      shortLinkStore.onOpenModal({
        onAddLink: onAddShortLink,
        editLink: trigger,
        onSuccess,
      })
    }

    const title = useMemo(() => {
      if (trigger) {
        return (
          <div className={styles.wrap}>
            <Button
              text={'Shorten URL'}
              icon={'link'}
              size={'small'}
              typeBtn={'text'}
              onClick={onShorten}
            />
            <div className={styles.divider} />
            <IconButton
              size={'small'}
              icon={'edit'}
              color={'secondary'}
              variant={'icon'}
              iconProps={{ fontSize: 16 }}
              onClick={onEdit}
              tooltipProps={{ label: 'Edit', placement: 'top', zIndex: ZIndex.OVERLAY_MIDDLE_100 }}
              ariaLabel={'ShortenLinkTooltip_edit'}
            />
          </div>
        )
      }
      return <></>
    }, [trigger])

    if (!trigger || !shortLinkStore.canShortUrl(trigger.href)) {
      return <></>
    }

    return (
      <TriggerTooltip
        trigger={trigger}
        onLeaveTooltip={onLeaveTooltip}
        refVisibleTooltip={refVisibleTooltip}
        color={'white'}
        component={title}
      />
    )
  }
)
